//#region Imports and Global Definitions
import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, Text, View, SafeAreaView, Platform, useWindowDimensions, FlatList } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomButton from '../../../components/CustomButton';
import CustomInput from '../../../components/CustomInput';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { ActivityIndicator, Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useTranslation } from 'react-i18next';
import { useTraining } from '../../../hooks/useTraining';
import moment from 'moment';
import { createCommonStyles } from '../../../styles/commonStyles';
import Modal from 'react-native-modal';
import { generateUniqueID } from '../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions';
import CustomPaging from '../../../components/CustomPaging/CustomPagining';
import RoleGuard from '../../../components/RoleGuard';
import { TASKS, TASK_OPTIONS } from '../../../constants/Tasks';
//#endregion

const HRAdminTrainingsScreen = (props) => {
    //#region Const Definitions
    const { trnData, setTRNData, subjects, categories } = props;
    const newCategoryOptions = props.categories.map(category => ({
        label: category.description,
        value: category.category
    }));
    const ref = useRef(null);
    const { t, i18n } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const [isLoading, setIsLoading] = useState(true);

    const { loadTrainings } = useTraining();
    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const [pagedItems, setPagedItems] = useState([]);

    // State variables for filter and sort
    const [isModalVisible, setModalVisible] = useState(false);
    const [isSorting, setIsSorting] = useState(false);
    const [filterCompleted, setFilterCompleted] = useState('all'); // 'all' or 'completed'
    const [sortBy, setSortBy] = useState('date'); // 'date' or 'title'
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
    const [filteredData, setFilteredData] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [filteredSubjectOptions, setFilteredSubjectOptions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // To update subject value when category is changed
    useEffect(() => {
        if (selectedCategory) {
            const newFilteredSubjects = subjects
                .filter(subject => subject.category === selectedCategory)
                .map(subject => ({
                    label: subject.description,
                    value: subject.subject
                }));
            setFilteredSubjectOptions(newFilteredSubjects);
        } else {
            setFilteredSubjectOptions([]);
        }
        setSelectedSubject(null);
    }, [selectedCategory, subjects]);
    //#endregion

    //#region UseEffects
    // Load Data
    useEffect(() => {
        loadTrainings().then((trn) => {
            if (trn.length > 0) {
                setTRNData(trn);
                setRefreshAfterSave(false);
                setIsLoading(false);
            } else {
                console.log(trn);
                showMessage({
                    message: t('common:loadTrainingsError'),
                    type: "danger",
                    duration: 3000,
                })
            }
        })
            .catch(() => {
                setRefreshAfterSave(false);
                setIsLoading(false);
            });
        setRefreshAfterSave(false);
        setIsLoading(false);
    }, [refreshAfterSave])

    // Filtering and sorting logic 
    useEffect(() => {
        let data = [...trnData];

        if (searchQuery.trim() !== '') {
            const lowerQuery = searchQuery.toLowerCase();
            data = data.filter(item => item.title.toLowerCase().includes(lowerQuery));
        }

        if (selectedCategory) {
            data = data.filter(item => item.category === selectedCategory);
        }

        if (selectedSubject) {
            data = data.filter(item => item.subject === selectedSubject);
        }

        // Filter
        if (filterCompleted === 'completed') {
            data = data.filter(item => item.isCompleted === true);
        }

        // Sort
        data.sort((a, b) => {
            let valA, valB;

            if (sortBy === 'date') {
                valA = new Date(a.dot);
                valB = new Date(b.dot);
            } else if (sortBy === 'title') {
                valA = a.title.toLowerCase();
                valB = b.title.toLowerCase();
            }

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredData(data);
    }, [trnData, filterCompleted, sortBy, sortOrder, selectedCategory, selectedSubject, searchQuery]);
    //#endregion

    //#region Render Trainings
    const renderTrainingItem = ({ item }) => {
        const formattedDate = moment(item.dot).format("MMM DD YYYY");
        return (
            <Card style={styles.cardstyle} onPress={() => {
                props.setIsEdit(true);
                props.setSelectedTraining(item);
                props.pressedOnTrainingDetails();
            }}>
                <Card.Content>
                    <SafeAreaView style={[styles.row, { alignItems: 'center' }]}>
                        <SafeAreaView style={{ flex: 6 }}>
                            <CustomLabel value={item.title} type="AVAILABLEUSED" />
                        </SafeAreaView>
                        <SafeAreaView style={{ flex: 2.5 }}>
                            <CustomLabel
                                value={formattedDate}
                                type='AVAILABLEUSED' />
                        </SafeAreaView>
                        <SafeAreaView style={{ flex: 1.5 }}>
                            <CustomLabel
                                value={item.isCompleted ? t('common:assessmentcomplete') : t('common:assessmentpending')}
                                type="STATUS"
                                specificColor={item.isCompleted ? colors.background_approved : colors.card_pending}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                </Card.Content>
            </Card>
        );
    };
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <SafeAreaView style={[styles.filterContainer, { justifyContent: 'space-between', alignItems: 'center' }]}>
                {/* New Course button on the far left */}
                <RoleGuard parentTask={TASKS.TRAINING} task={TASK_OPTIONS.TRAINEDITCOURSE} table='hr'  requireCRUD>
                    <CustomButton
                        onPress={() => {
                            props.setIsEdit(false);
                            props.setSelectedTraining(generateUniqueID());
                            props.pressedOnTrainingDetails();
                        }}
                        text={t('common:new')}
                        iconName='add-outline'
                        show='BORDERLESS_FIXEDWIDTH'
                        type='BORDERLESS_FIXEDWIDTH'
                        sz={25}
                    />
                </RoleGuard>
                {/* Search input between New button and filter/sort buttons */}
                <SafeAreaView style={{ width: '90%' }}>
                    <CustomInput
                        value={searchQuery}
                        editable={true}
                        setValue={(e) => { setSearchQuery(e) }}
                        type='SELECT_EMPLOYEE'
                        placeholder={`${t('common:search')}...`}
                    />
                </SafeAreaView>
                {/* Buttons For opening filtering and sorting modals */}
                <SafeAreaView style={{ flexDirection: 'row' }}>
                    <CustomButton
                        iconName='funnel-outline'
                        show='BORDERLESS_FIXEDWIDTH'
                        textTop='3'
                        sz={25}
                        type="BORDERLESS_FIXEDWIDTH"
                        onPress={() => {
                            setIsSorting(false);
                            setModalVisible(true);
                        }}
                    />
                    <CustomButton
                        svgName='arrows'
                        show='BORDERLESS_FIXEDWIDTH'
                        textTop='3'
                        sz={25}
                        type="BORDERLESS_FIXEDWIDTH"
                        onPress={() => {
                            setIsSorting(true);
                            setModalVisible(true);
                        }}
                    />
                </SafeAreaView>
            </SafeAreaView>
            {/* Filtering and sorting modal based on state variables */}
            <Modal
                isVisible={isModalVisible}
                hasBackdrop={true}
                backdropColor={colors.background_approved}
                backdropOpacity={0.9}
                deviceWidth={width}
                deviceHeight={height}
                onBackdropPress={() => setModalVisible(false)}
                onRequestClose={() => setModalVisible(false)}
            >
                <SafeAreaView style={{
                    width: (Platform.OS === 'web' && width > 1200) ? width / 6 : '95%',
                    alignSelf: 'center',
                    justifyContent: 'space-between'
                }}>
                    {isSorting ? (
                        <SafeAreaView>
                            <CustomButton
                                text={t('common:dateAsc')}
                                type={sortBy === 'date' && sortOrder === 'asc' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
                                onPress={() => {
                                    setSortBy('date');
                                    setSortOrder('asc');
                                }}
                            />
                            <CustomButton
                                text={t('common:dateDesc')}
                                type={sortBy === 'date' && sortOrder === 'desc' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
                                onPress={() => {
                                    setSortBy('date');
                                    setSortOrder('desc');
                                }}
                            />
                            <CustomButton
                                text={t('common:titleAsc')}
                                type={sortBy === 'title' && sortOrder === 'asc' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
                                onPress={() => {
                                    setSortBy('title');
                                    setSortOrder('asc');
                                }}
                            />
                            <CustomButton
                                text={t('common:titleDesc')}
                                type={sortBy === 'title' && sortOrder === 'desc' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
                                onPress={() => {
                                    setSortBy('title');
                                    setSortOrder('desc');
                                }}
                            />
                        </SafeAreaView>
                    ) : (
                        <SafeAreaView>
                            <CustomButton
                                text={t('common:all')}
                                type={filterCompleted === 'all' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
                                onPress={() => setFilterCompleted('all')}
                            />
                            <CustomButton
                                text={t('common:assessmentcomplete')}
                                type={filterCompleted === 'completed' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
                                onPress={() => setFilterCompleted('completed')}
                            />
                            {/* Category Dropdown */}
                            <CustomLabel value={t('common:category')} type="SUBTITLE" />
                            <CustomDropDown
                                options={newCategoryOptions}
                                defValue={selectedCategory}
                                value={selectedCategory}
                                setValue={setSelectedCategory}
                                placeholder={t('common:selectCategory')}
                                type='MYPROFILE'
                            />

                            {/* Subject Dropdown */}
                            <CustomLabel value={t('common:subject')} type="SUBTITLE" />
                            <CustomDropDown
                                options={filteredSubjectOptions}
                                value={selectedSubject}
                                defValue={selectedSubject}
                                setValue={setSelectedSubject}
                                placeholder={t('common:selectSubject')}
                                type='MYPROFILE'
                            />
                        </SafeAreaView>
                    )}
                    <SafeAreaView>
                        <CustomButton
                            type="BORDERLESS"
                            text={t('navigate:cancel')}
                            onPress={() => setModalVisible(false)}
                        />
                        <CustomButton
                            type="BORDERLESS"
                            text={isSorting ? t('navigate:sort') : t('navigate:filter')}
                            onPress={() => { setModalVisible(false); }}
                        />
                    </SafeAreaView>
                </SafeAreaView>
            </Modal>
            <SafeAreaView style={common.divider} />
            {/* Flatlist to render all trainings */}
            {trnData?.length > 0 ?
                <SafeAreaView style={styles.mainView}>
                    <FlatList
                        data={pagedItems}
                        keyExtractor={(item, index) => item.trnUI ? item.trnUI.toString() : index.toString()}
                        renderItem={renderTrainingItem}
                        contentContainerStyle={{ paddingBottom: 20 }}
                        ListHeaderComponent={
                            <SafeAreaView style={common.listTitle}>
                                <SafeAreaView style={[styles.rowHeaders]}>
                                    <SafeAreaView style={{ flex: 6 }}>
                                        <CustomLabel
                                            value={t('common:title')}
                                            type='SUBTITLE' />
                                    </SafeAreaView>
                                    <SafeAreaView style={{ flex: 2.5 }}>
                                        <CustomLabel
                                            value={t('common:date')}
                                            type='SUBTITLE' />
                                    </SafeAreaView>
                                    <SafeAreaView style={{ flex: 1.5 }}>
                                        <CustomLabel
                                            value={t('common:status')}
                                            type='SUBTITLE' />
                                    </SafeAreaView>
                                </SafeAreaView>
                                <SafeAreaView style={common.divider} />
                            </SafeAreaView>
                        }
                    />
                    <CustomPaging
                        items={filteredData}
                        itemsPerPage={20}
                        onPageItemsChange={setPagedItems}
                        searchable={true}
                    />
                </SafeAreaView>
                :
                <SafeAreaView style={styles.mainView}>
                    <Text style={[styles.textMain, { textAlign: 'center' }]}>{t('common:noTrainings')}</Text>
                </SafeAreaView>
            }
        </SafeAreaView>
    );
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
        marginVertical: Platform.OS === 'web' ? '0%' : 5,
        justifyContent: 'center'
    },
    textMain: {
        fontSize: Platform.OS === 'web' ? 18 : 14,
        color: colors.text_primary,
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Roboto-Medium',
    },
    cardstyle: {
        margin: 10,
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
    },
    row: {
        flex: 1,
        flexDirection: "row",
    },
    filterContainer: {
        flexDirection: 'row',
        alignSelf: 'stretch',
        paddingHorizontal: 10,
        alignItems: 'center',
        marginHorizontal: 15,
    },
    listTitle: {
        flex: 1,
        marginVertical: 10,
    },
    rowHeaders: {
        flexDirection: "row",
        marginHorizontal: '1%',
    },
});
//#endregion

export default HRAdminTrainingsScreen;